import { useI18n } from '../../lib/i18n';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import {
  GoogleSheetsIcon,
  GoogleDriveIcon,
  GoogleCalendarIcon,
  GoogleContactsIcon,
} from './GoogleServiceIcons';
import type { GoogleServicesStatus } from '../../lib/google';

interface ServicesGridProps {
  services: GoogleServicesStatus;
  hasAccounts: boolean;
  onImportContacts?: () => void;
}

export function ServicesGrid({
  services,
  hasAccounts,
  onImportContacts,
}: ServicesGridProps) {
  const { t } = useI18n();

  const items = [
    {
      key: 'sheets',
      name: t('google.services.sheetsTitle') || 'Google Sheets',
      icon: <GoogleSheetsIcon size={24} />,
      enabled: hasAccounts && services.sheets,
      description:
        t('google.services.sheetsDesc') ||
        'Sync subscribers automatically to Google Sheets. Use Sheets as dynamic data sources in bot flows.',
      scopesNote: t('google.services.sheetsScopes') || 'Read & Write spreadsheets',
    },
    {
      key: 'drive',
      name: t('google.services.driveTitle') || 'Google Drive',
      icon: <GoogleDriveIcon size={24} />,
      enabled: hasAccounts && services.drive,
      description:
        t('google.services.driveDesc') ||
        'Securely store and retrieve media attachments, campaign reports, and exported contact lists.',
      scopesNote: t('google.services.driveScopes') || 'App file storage access',
    },
    {
      key: 'calendar',
      name: t('google.services.calendarTitle') || 'Google Calendar',
      icon: <GoogleCalendarIcon size={24} />,
      enabled: hasAccounts && services.calendar,
      description:
        t('google.services.calendarDesc') ||
        'Schedule customer appointments and automatically generate Google Meet video conference links.',
      scopesNote: t('google.services.calendarScopes') || 'Calendar event management',
    },
    {
      key: 'contacts',
      name: t('google.services.contactsTitle') || 'Google Contacts',
      icon: <GoogleContactsIcon size={24} />,
      enabled: hasAccounts && services.contacts,
      description:
        t('google.services.contactsDesc') ||
        'Import contacts directly from Google People API into your WhatsApp subscriber lists with E.164 normalization.',
      scopesNote: t('google.services.contactsScopes') || 'Read-only contacts access',
      actionLabel: t('google.services.importContactsBtn') || 'Import Contacts',
      onAction: onImportContacts,
    },
  ];

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-bold text-ink dark:text-slate-100">
          {t('google.services.sectionTitle') || 'Integrated Services'}
        </h3>
        <p className="text-xs text-ink-3 dark:text-slate-400 mt-0.5">
          {t('google.services.sectionSubtitle') ||
            'Permissions and automated capabilities granted through your connected Google accounts.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((svc) => (
          <Card
            key={svc.key}
            className="flex flex-col justify-between p-4 border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900"
          >
            <div>
              <div className="flex items-start justify-between gap-2">
                <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shrink-0">
                  {svc.icon}
                </div>
                {svc.enabled ? (
                  <Badge variant="success" size="sm" dot>
                    {t('google.status.granted') || 'Granted'}
                  </Badge>
                ) : (
                  <Badge variant="slate" size="sm">
                    {hasAccounts
                      ? t('google.status.limited') || 'Limited'
                      : t('google.status.disconnected') || 'Not Connected'}
                  </Badge>
                )}
              </div>

              <div className="mt-3">
                <h4 className="text-xs font-bold text-ink dark:text-slate-100">
                  {svc.name}
                </h4>
                <p className="text-[11px] text-ink-3 dark:text-slate-400 mt-1 leading-relaxed">
                  {svc.description}
                </p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-ink-4 dark:text-slate-500">
              <span className="truncate">{svc.scopesNote}</span>
              {svc.onAction && svc.enabled && (
                <button
                  type="button"
                  onClick={svc.onAction}
                  className="font-bold text-brand hover:text-brand-600 dark:text-brand-tint shrink-0 ms-2 underline"
                >
                  {svc.actionLabel}
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
